import moment from 'moment';
import { filter, orderBy } from 'lodash';
import { User } from '@compassdigital/foodworks.ts.sdk';

export default class Format {
  /**
   * Parses the text and returns capitalize text
   * @param text
   */
  public static capitalize(text: string) {
    if (!text) return '';
    return text.replace(/\w\S*/g, value => value.charAt(0).toUpperCase() + value.substr(1).toLowerCase());
  }

  /**
   * Parses the phone and returns masked phone number
   * @param phone
   */
  public static maskPhoneNumber(phone: any) {
    const cleaned = (`''${phone}`).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return (`(${match[1]}) ${match[2]}-${match[3]}`);
    }
    return null;
  }

  /**
   * Parses the user object and returns formatted name
   * @param user
   */
  public static getFormattedName(user: any) {
    if (user && user.name && user.name.first && user.name.last) {
      return this.capitalize(`${user.name.first} ${user.name.last}`);
    }
    if (user && user.first_name && user.last_name) {
      return this.capitalize(`${user.first_name} ${user.last_name}`);
    }
    if (user && user.first && user.last) {
      return this.capitalize(`${user.first} ${user.last}`);
    }
    if (user && user.firstName && user.lastName) {
      return this.capitalize(`${user.firstName} ${user.lastName}`);
    }
    return '';
  }

  /**
   * Parses the uploaded date and returns formatted date
   * @param uploadDate
   */
  public static getFomattedDate(uploadDate: any) {
    return moment(uploadDate).format('MMMM. DD,YYYY');
  }

  /**
   * Parses the uploaded date and returns formatted date
   * @param uploadDate
   */
  public static getFomattedStats(uploadDate: any) {
    return moment(uploadDate).format('Do MMM YYYY');
  }

  /**
   * Parses the calender date and returns formatted date
   * @param calendarDate
   */
  public static getFomattedDateCalendar(calendarDate: any) {
    return moment(calendarDate).format('MMMM');
  }

  /**
   * Parses the calender date and returns formatted date
   * @param calendarDate
   */
  public static getCalendarTitle(calendarDate: any) {
    return moment(calendarDate).format('MMMM YYYY');
  }

  /** Parses the uploaded date and returns formatted date
   * @param uploadDate
   */
  public static getDatePickerFormatted(uploadDate: any) {
    return moment(uploadDate).format('YYYY-MM-DD');
  }

  /** Parses the uploaded date and returns formatted date
   * @param uploadDate
   */
  public static getDateDocs(uploadDate: any) {
    return moment(uploadDate).format('DD/MM/YYYY');
  }

  /** returns current formatted date
   */
  public static getCurrentDate() {
    return moment().format('YYYY-MM-DD');
  }

  /** returns current formatted date and time
   * @param timeStamp
   */
  public static getDateTime(timeStamp: number) {
    return moment(timeStamp).format('DD-MM-YYYY h:mm:ss');
  }

  /** Parses the date and returns formatted next month date
   * @param date
   */
  public static getNextMonth(date: any, format: string = 'YYYY-MM-DD') {
    return moment(date, format).add(1, 'months').format(format);
  }

  /** Parses the date and returns formatted prev month date
   * @param date
   */
  public static getPrevMonth(date: any, format: string = 'YYYY-MM-DD') {
    return moment(date, format).subtract(1, 'months').format(format);
  }

  /** Parses the uploaded date and returns formatted date
   * @param uploadDate
   */
  public static getSchedulerDate(uploadDate: any) {
    return moment(uploadDate).format('YYYY-MM');
  }

  /**
   * Parses the phone and returns unmasked phone number
   * @param phone
   */
  public static CheckMaskNumber(phone: any) {
    const checkMaskNumber: any = /^\d+$/;
    if (!checkMaskNumber.test(phone)) {
      let number = this.unMaskPhoneNumber(
        phone,
      );
      number = parseInt(number, 10);
      return number;
    }
    return parseInt(String(phone), 10);
  }

  /**
   * Parses the phone and returns unmasked phone number
   * @param phone
   */
  public static unMaskPhoneNumber(phone: any) {
    const updateNumber = phone.replace(
      new RegExp(/[^\d]/, 'g'),
      '',
    );
    return updateNumber;
  }

  /**
   * Parses the unsorted array and returns sorted array
   * @param array
   */
  public static sortedArray(newArray: any) {
    return orderBy(newArray, 'text', 'asc');
  }

  /**
   * Parses the users and returns formatted users list
   * @param users
   */
  public static usersListFormatted(users: Array<User>) {
    const mappedUsers = !users || users.map((user: User) => {
      const {
        id,
        name,
      } = user;

      let usersName = '';
      if (name && name.first && name.last) {
        usersName = Format.capitalize(`${name.first} ${name.last}`);
      }

      return {
        text: usersName,
        value: id,
      };
    });
    return orderBy(mappedUsers, 'text', 'asc');
  }

  /**
   * Parses the users and returns formatted users list
   * @param users
   */
  public static usersFwEmployee(users: User) {
    const { id, name } = users;
    let usersName = '';
    if (name && name.first && name.last) {
      usersName = Format.capitalize(`${name.first} ${name.last}`);
    }
    const fwEmployee: any[] = [];
    const employeeArray = {
      text: usersName,
      value: id,
    };
    fwEmployee.push(employeeArray);
    return fwEmployee;
  }

  /**
   * Parses the onBoarding user status and returns formatted status
   * @param userType
   */
  public static getOnBoardUserStatus(userType: string) {
    switch (userType) {
      case 'Partner':
        return 'partner_owner';
      default:
        return 'partner_owner';
    }
  }

  /**
   * Parses the onBoarding user status and returns formatted status
   * @param userType
   */
  public static getUserFormatStatus(userType: string) {
    switch (userType) {
      case 'partner_owner':
        return 'Partner';
      default:
        return 'Partner';
    }
  }

  /**
   * Parses the calender date and returns formatted day
   * @param calendarDate
   */
  public static getFormatDay(calendarDate: any) {
    return moment(calendarDate).format('dddd');
  }

  /**
   * Parses the cuisines array and returns formatted cuisine
   * @param cuisineType
   */
  public static getFormatCuisine(cuisineType: any) {
    if (cuisineType) {
      const cuisine = cuisineType.toString();
      return cuisine;
    }
    return null;
  }

  public static sortedItems(newArray: any) {
    return orderBy(newArray, [(user: any) => user.text.toLowerCase()], 'asc');
  }

  public static capitalizedText(text: string) {
    if (!text) return '';
    const capitalT = text.split('-').join(' ');
    const textNew = capitalT.replace(/\w\S*/g, value => value.charAt(0).toUpperCase() + value.substr(1).toLowerCase());
    return textNew;
  }

  /**
   * Parses the Partner type and returns formatted Partner type
   * @param type
   */
  public static getFormatType(type: string) {
    switch (type) {
      case 'restaurant':
        return 'Restaurant';
      case 'foodtruck':
        return 'Foodtruck';
      case 'apron_exchange':
        return 'Apron Exchange';
      default:
        return 'restaurant';
    }
  }
}
